import { handleError } from './apiUtils'
import { getConfigurationAsync, xKMSClient } from './httpMethods'
import {
  EntraIdRoleRequestDto,
  EntraIdRoleResponseDto,
  EntraIDRolesApi,
} from './kmsapiclient'

/**
 * Fetches the list of Entra ID roles asynchronously.
 *
 * @returns {Promise<Array<EntraIdRoleResponseDto>>} A promise that resolves to an array of EntraIdRoleResponseDto objects.
 *
 * @throws {Error} Throws an error if the request fails.
 */
export async function getEntraIdRolesAsync(): Promise<
  Array<EntraIdRoleResponseDto>
> {
  try {
    const entraIdRolesApi = new EntraIDRolesApi(getConfigurationAsync())
    const response = (await entraIdRolesApi.entraIdRolesGet(xKMSClient)).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Adds or updates the provided Entra ID roles asynchronously.
 *
 * @param {Array<EntraIdRoleRequestDto>} request - The request object containing the Entra ID roles to add or update.
 * @returns {Promise<Array<EntraIdRoleResponseDto>>} - A promise that resolves to the Entra ID roles response.
 */
export async function upsertEntraIdRolesAsync(
  request: Array<EntraIdRoleRequestDto>,
): Promise<Array<EntraIdRoleResponseDto>> {
  try {
    const entraIdRolesApi = new EntraIDRolesApi(getConfigurationAsync())
    const response = (
      await entraIdRolesApi.entraIdRolesUpsert(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Deletes the provided Entra ID roles asynchronously.
 *
 * @param {Array<EntraIdRoleRequestDto>} entraIdRoles - The request object containing the Entra ID roles to delete.
 * @returns {Promise<string>} - A promise that resolves to the status of the deletion.
 */
export async function deleteEntraIdRolesAsync(
  entraIdRoles: EntraIdRoleRequestDto[],
): Promise<string> {
  try {
    const ids = entraIdRoles.map((entraIdRole) => entraIdRole.id) as string[]
    const entraIdRolesApi = new EntraIDRolesApi(getConfigurationAsync())
    const response = (await entraIdRolesApi.entraIdRolesDelete(ids, xKMSClient))
      .data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}
